import { err, Result } from "neverthrow";
import { UserProfileModel } from "./models";
import { DomainError } from "@/lib/api/domain-error";
import type { RequestFn } from "@/lib/api/types";

const ONBOARDING_ROUTE_V1 = "/v1/onboarding";

export type OnboardingStatus = {
  accountSlug: string | null;
  essentialCompleted: boolean;
  profileCompleted: boolean;
  nextStep:
    | "USER_IDENTIFICATION"
    | "ADDRESS"
    | "USAGE_PROFILE"
    | "WORK_FOCUS"
    | "CURRENT_TOOLSET"
    | "PAIN_POINTS"
    | "COMPLETED";
};

export default class OnboardingRepository {
  constructor(private readonly request: RequestFn) {}

  async getStatus(): Promise<Result<OnboardingStatus, DomainError>> {
    try {
      const apiResult = await this.request({
        url: `${ONBOARDING_ROUTE_V1}/status`,
        method: "GET",
      });
      return apiResult.map((domainData) => domainData.data as OnboardingStatus);
    } catch (error) {
      return err(new DomainError(error));
    }
  }

  async getUserprofile(): Promise<Result<UserProfileModel, DomainError>> {
    try {
      const url = `${ONBOARDING_ROUTE_V1}/user-profile`;
      const apiResult = await this.request({
        url,
        method: "GET",
      });

      return apiResult.map((domainData: any) => domainData.data);
    } catch (error) {
      return err(new DomainError(error));
    }
  }

  async createIdentification(
    name: string,
  ): Promise<Result<String, DomainError>> {
    try {
      const url = `${ONBOARDING_ROUTE_V1}/identification`;
      const apiResult = await this.request({
        url,
        method: "POST",
        data: {
          name,
        },
      });

      return apiResult.map((domainData: any) => domainData.data);
    } catch (error) {
      return err(new DomainError(error));
    }
  }

  async createPerson(person: any): Promise<Result<any, DomainError>> {
    try {
      const url = `${ONBOARDING_ROUTE_V1}/address`;
      const apiResult = await this.request({
        url,
        method: "POST",
        data: person,
      });

      return apiResult.map((domainData: any) => domainData.data);
    } catch (error) {
      return err(new DomainError(error));
    }
  }

  async createPlatformUsage(
    platformUsage: string,
  ): Promise<Result<String, DomainError>> {
    try {
      const url = `${ONBOARDING_ROUTE_V1}/platformusage`;
      const apiResult = await this.request({
        url,
        method: "POST",
        data: { platformUsage: platformUsage },
      });

      return apiResult.map((domainData: any) => domainData.data);
    } catch (error) {
      return err(new DomainError(error));
    }
  }

  async createFocusAreas(
    focusAreas: string[],
  ): Promise<Result<String, DomainError>> {
    try {
      const url = `${ONBOARDING_ROUTE_V1}/focusAreas`;
      const apiResult = await this.request({
        url,
        method: "POST",
        data: { focusAreas: focusAreas },
      });

      return apiResult.map((domainData: any) => domainData.data);
    } catch (error) {
      return err(new DomainError(error));
    }
  }

  async createTools(tools: string[]): Promise<Result<String[], DomainError>> {
    try {
      const url = `${ONBOARDING_ROUTE_V1}/currentTools`;
      const apiResult = await this.request({
        url,
        method: "POST",
        data: { currentTools: tools },
      });

      return apiResult.map((domainData: any) => domainData.data);
    } catch (error) {
      return err(new DomainError(error));
    }
  }

  async createPainPoint(
    painPoint: string,
  ): Promise<Result<String[], DomainError>> {
    try {
      const url = `${ONBOARDING_ROUTE_V1}/painPoint`;
      const apiResult = await this.request({
        url,
        method: "POST",
        data: { painPoint: painPoint },
      });

      return apiResult.map((domainData: any) => domainData.data);
    } catch (error) {
      return err(new DomainError(error));
    }
  }

  async getMessageTest(): Promise<Result<String, DomainError>> {
    try {
      const url = "authpublic/message";
      const apiResult = await this.request({
        url,
        method: "GET",
      });

      return apiResult.map((domainData: any) => domainData.data);
    } catch (error) {
      return err(new DomainError(error));
    }
  }
}
